import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Plus, X, Upload } from 'lucide-react';
import { toast } from 'sonner';

const HotelOnboardingForm = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    // Basic Property Information
    property_name: '',
    city: '',
    state: '',
    property_type: 'Hotel',
    address: '',
    pincode: '',
    landmark: '',
    phone: '',
    alternate_number: '',
    email: '',
    website: '',
    total_rooms: '',
    
    // Ownership & Contact Details
    owner_name: '',
    owner_phone: '',
    owner_email: '',
    contact_person_name: '',
    contact_person_designation: '',
    contact_person_number: '',
    contact_person_email: '',
    
    // Financial & Commission Info
    bank_name: '',
    account_holder_name: '',
    account_number: '',
    ifsc_code: '',
    pan_number: '',
    gst_number: '',
    payment_frequency: 'Monthly',
    commission_agreed: '',
    token_amount: '',
    setup_agreed_amount: '',
    
    // Agreement & Setup Details
    grb_agreement_date: '',
    grb_setup_completion_status: 'Pending',
    ota_live_date: '',
    first_reservation_date: '',
    salesperson_name: '',
    property_status: 'Active',
  });

  const [roomTypes, setRoomTypes] = useState([
    {
      name: '',
      max_occupancy: 2,
      base_rate: '',
      breakfast_included: false,
      extra_bed_available: false,
      extra_bed_price: '',
      description: ''
    }
  ]);

  const [files, setFiles] = useState({
    property_photos: [],
    room_photos: [],
    logo: null,
    documents: []
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e, fileType) => {
    const selectedFiles = Array.from(e.target.files);
    if (fileType === 'logo') {
      setFiles(prev => ({ ...prev, logo: selectedFiles[0] || null }));
    } else {
      setFiles(prev => ({ ...prev, [fileType]: selectedFiles }));
    }
  };

  const addRoomType = () => {
    setRoomTypes(prev => [
      ...prev,
      {
        name: '',
        max_occupancy: 2,
        base_rate: '',
        breakfast_included: false,
        extra_bed_available: false,
        extra_bed_price: '',
        description: ''
      }
    ]);
  };

  const removeRoomType = (index) => {
    if (roomTypes.length > 1) {
      setRoomTypes(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleRoomTypeChange = (index, field, value) => {
    setRoomTypes(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.property_name.trim()) {
      toast.error('Property name is required');
      return;
    }
    if (!formData.email || !formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      toast.error('Valid email is required');
      return;
    }
    if (!formData.phone.trim()) {
      toast.error('Phone number is required');
      return;
    }
    if (!formData.total_rooms || parseInt(formData.total_rooms) < 1) {
      toast.error('Total rooms must be at least 1');
      return;
    }
    if (!formData.owner_name.trim()) {
      toast.error('Owner name is required');
      return;
    }
    if (!formData.owner_email || !formData.owner_email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      toast.error('Valid owner email is required');
      return;
    }
    
    // Validate room types
    const validRoomTypes = roomTypes.filter(rt => rt.name.trim() && rt.base_rate);
    if (validRoomTypes.length === 0) {
      toast.error('At least one valid room type is required (name and rate)');
      return;
    }

    // Prepare FormData for multipart submission
    const submitData = new FormData();
    
    // Append form fields
    Object.keys(formData).forEach(key => {
      if (formData[key] !== null && formData[key] !== '') {
        submitData.append(key, formData[key]);
      }
    });
    
    // Append room types as JSON
    submitData.append('room_types_json', JSON.stringify(validRoomTypes));
    
    // Append files
    if (files.property_photos.length > 0) {
      files.property_photos.forEach(file => {
        submitData.append('property_photos', file);
      });
    }
    if (files.room_photos.length > 0) {
      files.room_photos.forEach(file => {
        submitData.append('room_photos', file);
      });
    }
    if (files.logo) {
      submitData.append('logo', files.logo);
    }
    if (files.documents.length > 0) {
      files.documents.forEach(file => {
        submitData.append('documents', file);
      });
    }

    await onSubmit(submitData);
  };

  return (
    <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-2xl font-bold text-gray-800">
          Hotel Onboarding Form
        </DialogTitle>
      </DialogHeader>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Section 1: Basic Property Information */}
        <div className="border-b pb-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            1. Basic Property Information
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Property Name *
              </label>
              <Input
                name="property_name"
                value={formData.property_name}
                onChange={handleInputChange}
                placeholder="Grand Hotel & Resort"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City *
              </label>
              <Input
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                placeholder="Mumbai"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State *
              </label>
              <Input
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                placeholder="Maharashtra"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Property Type *
              </label>
              <Select value={formData.property_type} onValueChange={(val) => handleSelectChange('property_type', val)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Hotel">Hotel</SelectItem>
                  <SelectItem value="Resort">Resort</SelectItem>
                  <SelectItem value="Homestay">Homestay</SelectItem>
                  <SelectItem value="Villa">Villa</SelectItem>
                  <SelectItem value="Hostel">Hostel</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pincode *
              </label>
              <Input
                name="pincode"
                value={formData.pincode}
                onChange={handleInputChange}
                placeholder="400001"
                required
              />
            </div>
            
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address *
              </label>
              <Input
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="123 Main Street"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Landmark
              </label>
              <Input
                name="landmark"
                value={formData.landmark}
                onChange={handleInputChange}
                placeholder="Near City Mall"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Number *
              </label>
              <Input
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+91-22-12345678"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Alternate Number
              </label>
              <Input
                name="alternate_number"
                value={formData.alternate_number}
                onChange={handleInputChange}
                placeholder="+91-22-87654321"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address *
              </label>
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="contact@hotel.com"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Website
              </label>
              <Input
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                placeholder="www.hotel.com"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total Rooms *
              </label>
              <Input
                name="total_rooms"
                type="number"
                value={formData.total_rooms}
                onChange={handleInputChange}
                placeholder="50"
                min="1"
                required
              />
            </div>
          </div>
        </div>

        {/* Section 2: Ownership & Contact Details */}
        <div className="border-b pb-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            2. Ownership & Contact Details
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Owner Name *
              </label>
              <Input
                name="owner_name"
                value={formData.owner_name}
                onChange={handleInputChange}
                placeholder="John Doe"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Owner Phone *
              </label>
              <Input
                name="owner_phone"
                value={formData.owner_phone}
                onChange={handleInputChange}
                placeholder="+91-98765-43210"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Owner Email *
              </label>
              <Input
                name="owner_email"
                type="email"
                value={formData.owner_email}
                onChange={handleInputChange}
                placeholder="owner@hotel.com"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Person Name
              </label>
              <Input
                name="contact_person_name"
                value={formData.contact_person_name}
                onChange={handleInputChange}
                placeholder="Jane Smith"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Person Designation
              </label>
              <Input
                name="contact_person_designation"
                value={formData.contact_person_designation}
                onChange={handleInputChange}
                placeholder="Manager"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Person Number
              </label>
              <Input
                name="contact_person_number"
                value={formData.contact_person_number}
                onChange={handleInputChange}
                placeholder="+91-98765-43210"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Person Email
              </label>
              <Input
                name="contact_person_email"
                type="email"
                value={formData.contact_person_email}
                onChange={handleInputChange}
                placeholder="manager@hotel.com"
              />
            </div>
          </div>
        </div>

        {/* Section 3: Financial & Commission Info */}
        <div className="border-b pb-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            3. Financial & Commission Info
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bank Name
              </label>
              <Input
                name="bank_name"
                value={formData.bank_name}
                onChange={handleInputChange}
                placeholder="State Bank of India"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account Holder Name
              </label>
              <Input
                name="account_holder_name"
                value={formData.account_holder_name}
                onChange={handleInputChange}
                placeholder="John Doe"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account Number
              </label>
              <Input
                name="account_number"
                value={formData.account_number}
                onChange={handleInputChange}
                placeholder="1234567890"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                IFSC Code
              </label>
              <Input
                name="ifsc_code"
                value={formData.ifsc_code}
                onChange={handleInputChange}
                placeholder="SBIN0001234"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                PAN Number
              </label>
              <Input
                name="pan_number"
                value={formData.pan_number}
                onChange={handleInputChange}
                placeholder="ABCDE1234F"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                GST Number
              </label>
              <Input
                name="gst_number"
                value={formData.gst_number}
                onChange={handleInputChange}
                placeholder="22ABCDE1234F1Z5"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Frequency
              </label>
              <Select value={formData.payment_frequency} onValueChange={(val) => handleSelectChange('payment_frequency', val)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Weekly">Weekly</SelectItem>
                  <SelectItem value="Biweekly">Biweekly</SelectItem>
                  <SelectItem value="Monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Commission Agreed (%)
              </label>
              <Input
                name="commission_agreed"
                type="number"
                step="0.01"
                value={formData.commission_agreed}
                onChange={handleInputChange}
                placeholder="15"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Token Amount (₹)
              </label>
              <Input
                name="token_amount"
                type="number"
                step="0.01"
                value={formData.token_amount}
                onChange={handleInputChange}
                placeholder="5000"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Setup Agreed Amount (₹)
              </label>
              <Input
                name="setup_agreed_amount"
                type="number"
                step="0.01"
                value={formData.setup_agreed_amount}
                onChange={handleInputChange}
                placeholder="10000"
              />
            </div>
          </div>
        </div>

        {/* Section 4: Agreement & Setup Details */}
        <div className="border-b pb-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            4. Agreement & Setup Details
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                GRB Agreement Date
              </label>
              <Input
                name="grb_agreement_date"
                type="date"
                value={formData.grb_agreement_date}
                onChange={handleInputChange}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                GRB Setup Completion Status
              </label>
              <Select value={formData.grb_setup_completion_status} onValueChange={(val) => handleSelectChange('grb_setup_completion_status', val)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                OTA Live Date
              </label>
              <Input
                name="ota_live_date"
                type="date"
                value={formData.ota_live_date}
                onChange={handleInputChange}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Reservation Date
              </label>
              <Input
                name="first_reservation_date"
                type="date"
                value={formData.first_reservation_date}
                onChange={handleInputChange}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Salesperson Name
              </label>
              <Input
                name="salesperson_name"
                value={formData.salesperson_name}
                onChange={handleInputChange}
                placeholder="Sales Representative"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Property Status
              </label>
              <Select value={formData.property_status} onValueChange={(val) => handleSelectChange('property_status', val)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Hold">Hold</SelectItem>
                  <SelectItem value="Terminated">Terminated</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Section 5: Room Types (Repeater) */}
        <div className="border-b pb-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-800">
              5. Room Types *
            </h3>
            <Button type="button" onClick={addRoomType} size="sm" className="flex items-center gap-2">
              <Plus size={16} /> Add Room Type
            </Button>
          </div>
          
          <div className="space-y-4">
            {roomTypes.map((roomType, index) => (
              <div key={index} className="border rounded-lg p-4 bg-gray-50 relative">
                {roomTypes.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeRoomType(index)}
                    className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                  >
                    <X size={16} />
                  </Button>
                )}
                
                <h4 className="font-medium text-gray-700 mb-3">Room Type {index + 1}</h4>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Room Type Name *
                    </label>
                    <Input
                      value={roomType.name}
                      onChange={(e) => handleRoomTypeChange(index, 'name', e.target.value)}
                      placeholder="Deluxe Room"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Occupancy *
                    </label>
                    <Input
                      type="number"
                      value={roomType.max_occupancy}
                      onChange={(e) => handleRoomTypeChange(index, 'max_occupancy', parseInt(e.target.value))}
                      placeholder="2"
                      min="1"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Base Rate (₹) *
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      value={roomType.base_rate}
                      onChange={(e) => handleRoomTypeChange(index, 'base_rate', e.target.value)}
                      placeholder="2000"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Breakfast Included
                    </label>
                    <Select 
                      value={roomType.breakfast_included ? "true" : "false"} 
                      onValueChange={(val) => handleRoomTypeChange(index, 'breakfast_included', val === "true")}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">Yes</SelectItem>
                        <SelectItem value="false">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Extra Bed Available
                    </label>
                    <Select 
                      value={roomType.extra_bed_available ? "true" : "false"} 
                      onValueChange={(val) => handleRoomTypeChange(index, 'extra_bed_available', val === "true")}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">Yes</SelectItem>
                        <SelectItem value="false">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Extra Bed Price (₹)
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      value={roomType.extra_bed_price}
                      onChange={(e) => handleRoomTypeChange(index, 'extra_bed_price', e.target.value)}
                      placeholder="500"
                      disabled={!roomType.extra_bed_available}
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <Input
                      value={roomType.description}
                      onChange={(e) => handleRoomTypeChange(index, 'description', e.target.value)}
                      placeholder="Brief description of room type"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Section 6: Media Uploads */}
        <div className="border-b pb-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            6. Media Uploads
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Upload size={16} className="inline mr-2" />
                Property Photos
              </label>
              <Input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handleFileChange(e, 'property_photos')}
                className="cursor-pointer"
              />
              <p className="text-xs text-gray-500 mt-1">
                {files.property_photos.length} file(s) selected
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Upload size={16} className="inline mr-2" />
                Room Photos
              </label>
              <Input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handleFileChange(e, 'room_photos')}
                className="cursor-pointer"
              />
              <p className="text-xs text-gray-500 mt-1">
                {files.room_photos.length} file(s) selected
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Upload size={16} className="inline mr-2" />
                Logo
              </label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e, 'logo')}
                className="cursor-pointer"
              />
              <p className="text-xs text-gray-500 mt-1">
                {files.logo ? '1 file selected' : 'No file selected'}
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Upload size={16} className="inline mr-2" />
                Documents (PDF)
              </label>
              <Input
                type="file"
                accept=".pdf"
                multiple
                onChange={(e) => handleFileChange(e, 'documents')}
                className="cursor-pointer"
              />
              <p className="text-xs text-gray-500 mt-1">
                {files.documents.length} file(s) selected
              </p>
            </div>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="flex-1 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700"
          >
            Save Property
          </Button>
        </div>
      </form>
    </DialogContent>
  );
};

export default HotelOnboardingForm;
